import { CSSProperties, useContext, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import BgTop from "./../../shared/assets/imgs/bg_login_top.svg";
import BgBottom from "./../../shared/assets/imgs/bg_login_bottom.svg";
import Logo from "../../components/Logo/Logo";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../shared/router/routes";
import { AuthenticationContext } from "../../shared/providers/authentication/authentication.provider";
import InputText from "../../components/Forms/InputText/InputText";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import {
    IAuthTokens,
    ILogin,
    IRequestError
} from "../../shared/interfaces/authentication.interface";
import { getCookies } from "../../shared/helpers/utils";
import { COOKIES } from "../../shared/constants";
import { useAuthLogin } from "../../shared/hooks/useAuthentication";
import Button from "../../components/Button/Button";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import LanguagePicker from "../../components/LanguagePicker/LanguangePicker";

function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { translate } = useContext(TranslationContext);
    const { setIsAuthenticated } = useContext(AuthenticationContext);

    useEffect(() => {
        document.body.classList.add("is_login");

        const cookies = getCookies();
        if (cookies[COOKIES.REFRESH_TOKEN]) {
            navigate(routes.HOME.path);
        } else {
            setIsAuthenticated(false);
        }

        return () => {
            document.body.classList.remove("is_login");
        };
    }, []);

    const showBack = useMemo(() => {
        const display = [
            routes.FORGOT_PASSWORD.path
        ];

        return display.includes(location.pathname);
    }, [location]);

    return (
        <div
            style={
                {
                    "--bg-top": `url(${BgTop})`,
                    "--bg-bottom": `url(${BgBottom})`
                } as CSSProperties
            }
            className="login d-flex flex-column gap-22 justify-content-center align-items-center p-1">
            <div className="container">
                <Logo isWhite className="align-self-start d-md-none mt-2 mb-4" />
                {showBack && (
                    <Link
                        to="/"
                        className="mt-4 mt-md-0 d-flex align-items-center gap-02 text-bd2-rg color-neutrals-white">
                        <i className="icon icon-16 icon-Chevron--left"></i>
                        {translate("common.back", "Back")}
                    </Link>
                )}
            </div>
            <div className="container login_form d-flex flex-column align-items-center gap-4 gap-md-23">
                <Outlet />
            </div>
            <LanguagePicker isInline />
        </div>
    );
}

export function LoginForm() {
    const login = useAuthLogin();
    const navigate = useNavigate();
    const { setAuthTokens, setIsAuthenticated } = useContext(AuthenticationContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { translate } = useContext(TranslationContext);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [errorCode, setErrorCode] = useState<string>();

    const form = useFormik({
        initialValues: {
            email: "",
            password: ""
        },
        validationSchema: Yup.object().shape({
            email: Yup.string().required("Email is required"),
            password: Yup.string().required("Password is required")
        }),
        validateOnChange: false,
        onSubmit: async (values: ILogin) => {
            setIsLoading(true);
            const response = await login.mutateAsync(values);
            if (response.success) {
                const data = response.data as IAuthTokens;
                setAuthTokens(data);
                setIsAuthenticated(true);
                navigate(routes.HOME.path);
            } else {
                const data = response.data as IRequestError;
                setErrorCode(data.code);
                setErrorMessage(data.message);
                setIsAuthenticated(false);
            }
            setIsLoading(false);
        }
    });

    return (
        <>
            <Logo className="d-none d-md-block" />
            <div className="d-flex flex-column gap-22 gap-md-2 w-100">
                <h1 className="d-md-none text-h1-big-bd login_title">
                    {translate("page.login.title_mobile", "Login")}
                </h1>
                <h1 className="d-none d-md-block text-h1-big-bd login_title w-100 text-md-center">
                    {translate("page.login.title", "Login")}
                </h1>
                <form className="form d-flex flex-column gap-12" onSubmit={form.handleSubmit}>
                    {errorCode && (
                        <span className="mt-n02 color-red-600 text-bd4-md mb-n1">
                            {translate(`auth.code.${errorCode}`, errorMessage)}
                        </span>
                    )}
                    <InputText
                        name="email"
                        inputMode="email"
                        label={translate("common.email", "Email")}
                        placeholder={translate("form.placeholder.email", "Insert email")}
                        type="email"
                        errors={form.submitCount > 0 ? form.errors.email?.split("#") : []}
                        onChange={form.handleChange}
                    />
                    <InputText
                        name="password"
                        label={translate("common.password", "Password")}
                        placeholder={translate("form.placeholder.password", "Insert password")}
                        type="password"
                        errors={form.submitCount > 0 ? form.errors.password?.split("#") : []}
                        onChange={form.handleChange}
                    />

                    <Link
                        to={routes.FORGOT_PASSWORD.path}
                        className="login_link text-bd2-bd text-center ms-auto">
                        {translate("page.login.forgot_password", "Forgot your password?")}
                    </Link>
                    <Button
                        type="submit"
                        variant="solid"
                        customCss="btn_action mt-02 align-self-center"
                        label={translate("common.login", "Login")}
                    />
                </form>
            </div>
        </>
    );
}

export default Login;
